// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Minimum date allowed for date inputs across the application.
 * Set to January 1, 1970 (Unix epoch) to prevent issues with early dates.
 */
export const MINIMUM_DATE = DateTime.fromObject({
  year: 1970,
  month: 1,
  day: 1,
});

/**
 * Maximum date allowed for date inputs across the application.
 * Set to December 31, 2099 to prevent overflow issues with timestamps.
 */
export const MAXIMUM_DATE = DateTime.fromObject({
  year: 2099,
  month: 12,
  day: 31,
});

function isOnOrAfterApplicationMinimumDate(
  value: DateTime<boolean> | null
): boolean {
  return value === null || !value.isValid || value >= MINIMUM_DATE;
}

function isOnOrBeforeApplicationMaximumDate(
  value: DateTime<boolean> | null
): boolean {
  return value === null || !value.isValid || value <= MAXIMUM_DATE;
}

export function applyNullableApplicationDateRange<
  Output extends DateTime | null,
  Input,
>(schema: z.ZodType<Output, z.ZodTypeDef, Input>, fieldLabel: string) {
  return schema
    .refine(isOnOrAfterApplicationMinimumDate, {
      message: `${fieldLabel} cannot be before January 1, 1970`,
    })
    .refine(isOnOrBeforeApplicationMaximumDate, {
      message: `${fieldLabel} cannot be after December 31, 2099`,
    });
}

/**
 * Interface for single date shortcuts.
 * Used for date pickers that select a single date rather than a date range.
 */
export interface DateShortcut {
  label: string;
  getDate: (today: DateTime) => DateTime;
}

/**
 * Today's date shortcut.
 */
const DATE_SHORTCUT_TODAY: DateShortcut = {
  label: 'Today',
  getDate: (today) => today,
};

/**
 * Yesterday's date shortcut.
 */
const DATE_SHORTCUT_YESTERDAY: DateShortcut = {
  label: 'Yesterday',
  getDate: (today) => today.minus({ days: 1 }),
};

/**
 * End of last month shortcut.
 */
const DATE_SHORTCUT_END_OF_LAST_MONTH: DateShortcut = {
  label: 'End of last month',
  getDate: (today) => today.minus({ months: 1 }).endOf('month'),
};

/**
 * End of last quarter shortcut.
 */
const DATE_SHORTCUT_END_OF_LAST_QUARTER: DateShortcut = {
  label: 'End of last quarter',
  getDate: (today) => today.minus({ quarters: 1 }).endOf('quarter'),
};

/**
 * End of last year shortcut.
 */
const DATE_SHORTCUT_END_OF_LAST_YEAR: DateShortcut = {
  label: 'End of last year',
  getDate: (today) => today.minus({ years: 1 }).endOf('year'),
};

/**
 * Standard collection of date shortcuts for historical financial reporting.
 * Focuses on historical period endings that are commonly used in asset snapshots and financial reports.
 * Only includes past dates since inventory snapshots can only be taken for historical periods.
 */
export const HISTORICAL_PERIOD_DATE_SHORTCUTS: DateShortcut[] = [
  DATE_SHORTCUT_TODAY,
  DATE_SHORTCUT_YESTERDAY,
  DATE_SHORTCUT_END_OF_LAST_MONTH,
  DATE_SHORTCUT_END_OF_LAST_QUARTER,
  DATE_SHORTCUT_END_OF_LAST_YEAR,
];

/**
 * A date shortcut expressed as an offset (in days) from an anchor date.
 * Used for one-click date selection buttons rendered under date inputs.
 */
export interface RelativeDateShortcut {
  /**
   * The compact label shown on the shortcut button (e.g. "Today", "+1d").
   */
  label: string;

  /**
   * The number of days to add to the anchor date when the shortcut is
   * clicked. Zero selects the anchor date itself.
   */
  days: number;
}

/**
 * Shortcuts for promise ship dates, anchored to today. Covers every promise
 * window from same day out to one week.
 */
export const PROMISE_SHIP_DATE_SHORTCUTS: RelativeDateShortcut[] = [
  { label: 'Today', days: 0 },
  { label: '+1d', days: 1 },
  { label: '+2d', days: 2 },
  { label: '+3d', days: 3 },
  { label: '+4d', days: 4 },
  { label: '+5d', days: 5 },
  { label: '+6d', days: 6 },
  { label: '+7d', days: 7 },
];

/**
 * Returns shortcuts for promise delivery dates, anchored to the promise ship
 * date when set (otherwise today). The zero-offset entry selects the anchor
 * date itself, labeled "Ship" when a ship date anchors the row and "Today"
 * when the row falls back to today.
 */
export function getPromiseDeliveryDateShortcuts(
  hasShipDate: boolean
): RelativeDateShortcut[] {
  return [
    { label: hasShipDate ? 'Ship' : 'Today', days: 0 },
    { label: '+1d', days: 1 },
    { label: '+2d', days: 2 },
    { label: '+3d', days: 3 },
    { label: '+4d', days: 4 },
    { label: '+5d', days: 5 },
    { label: '+6d', days: 6 },
    { label: '+7d', days: 7 },
  ];
}
